/**
 * ArrivalScreen - behavior for the static #arrival-screen shell in index.html
 * (the unified entry screen that replaced the separate login menu + waiting
 * room). One screen, one "Enter Oggi" button: display name, avatar color,
 * an OPTIONAL camera/mic check, and a live "who's already here" line.
 *
 * Division of labor: loginFlow.js keeps the invite gate + session resolution
 * and owns the Enter click; this module owns the identity/device widgets.
 * The Agora-heavy device check internals live in arrivalDeviceCheck.js and
 * are imported only when the user clicks "Enable camera & mic", so the SDK
 * stays out of the boot chunk exactly as the old waiting room kept it.
 *
 * Entering never requires device permission: collect() falls back to
 * `{ audioOnly: false, micMuted: false }` (the historical default join
 * semantics) when the device check was never opened.
 */
import { CONFIG } from '../config/client.config.js';
import {
  getPlayerColor,
  setPlayerColor,
  isValidPlayerColor,
  randomAvatarColor,
} from '../services/playerColor.js';
import { apiService } from '../services/api.js';
import { formatPresenceLine } from '../services/presenceLine.js';
import { setIdentity } from '../state/sessionStore';

// Presence poll cadence - matches the server-side liveness granularity
// (30s heartbeats / 75s window) closely enough to feel live.
const PRESENCE_POLL_MS = 10_000;

/**
 * Wire up the arrival screen widgets. Call once from initLoginFlow().
 * @returns {{
 *   onGatePassed: (opts: {roomName?: string, preview?: boolean}) => void,
 *   collect: () => {color: string|null, deviceCheckUsed: boolean, prefs: object},
 *   showLobby: (opts: Object) => void,
 *   hideLobby: () => void,
 * }}
 */
export function initArrivalScreen() {
  const meta = CONFIG.ARRIVAL || {};
  let deviceCheck = null; // { getPrefs, teardown } once the lazy module mounted
  let deviceCheckUsed = false;
  let presenceTimer = null;
  let closed = false; // guards async work resolving after collect()
  let podPreview = null; // mounted arrivalPodPreview handle (3D self-preview)
  let podPromise = null; // in-flight/settled pod mount, resolves handle|null
  let podFatalCb = null; // device check's flat-preview restore on context loss

  // --- session metadata slots (all user/config text via textContent) -------
  const sessionTitle = document.getElementById('arrival-session-title');
  if (sessionTitle && meta.meetingTitle) {
    sessionTitle.textContent = meta.meetingTitle;
    sessionTitle.style.display = '';
  }
  const privacy = document.getElementById('arrival-privacy');
  if (privacy && meta.privacyNote) {
    privacy.textContent = meta.privacyNote;
    privacy.style.display = '';
  }

  // --- avatar color picker -------------------------------------------------
  // The highlighted swatch is exactly the color the avatar will get: stored
  // choice if present, else a random palette pick that is persisted at
  // Enter time (so the preview never lies about the join color).
  const picker = document.getElementById('arrival-color-picker');
  const swatches = picker ? Array.from(picker.querySelectorAll('.arrival-color-swatch')) : [];
  let selectedColor = getPlayerColor() || randomAvatarColor();
  let colorExplicitlyChosen = !!getPlayerColor();

  function paintSwatches() {
    for (const btn of swatches) {
      btn.setAttribute('aria-pressed', btn.dataset.color === selectedColor ? 'true' : 'false');
    }
  }
  paintSwatches();
  for (const btn of swatches) {
    btn.addEventListener('click', () => {
      if (!isValidPlayerColor(btn.dataset.color)) return;
      selectedColor = btn.dataset.color;
      colorExplicitlyChosen = true;
      setPlayerColor(selectedColor);
      paintSwatches();
    });
  }

  // --- optional device check (lazy Agora chunk) ----------------------------
  const deviceGroup = document.getElementById('arrival-device-group');
  const enableBtn = document.getElementById('arrival-device-enable');
  const permText = document.querySelector('#arrival-perm p');

  if (enableBtn) {
    enableBtn.addEventListener('click', async () => {
      if (deviceCheck || closed) return;
      enableBtn.disabled = true;
      enableBtn.textContent = 'Requesting access…';
      try {
        const { mountDeviceCheck } = await import('./arrivalDeviceCheck.js');
        if (closed) return;
        deviceCheck = await mountDeviceCheck({
          pod: {
            get: () => podPreview,
            ready: () => podPromise || Promise.resolve(null),
            onFatal: (cb) => {
              podFatalCb = cb;
            },
          },
        });
        deviceCheckUsed = true;
      } catch (err) {
        console.warn('[Arrival] Device check failed to load:', err);
        if (permText) {
          permText.textContent =
            'The device check could not start. You can still enter - camera and mic can be enabled from the in-room controls.';
        }
        enableBtn.style.display = 'none';
      }
    });
  }

  // --- POD self-preview (lazy Babylon chunk) -------------------------------
  // Mounted as soon as the gate passes so the pod greets the user immediately
  // (standby disc until devices are enabled). The device check feeds it
  // camera tracks via the `pod` accessor passed to mountDeviceCheck; any
  // failure resolves null and the flat Agora preview flow takes over.
  function startPodPreview() {
    if (podPromise) return; // the lobby path can pass the gate twice
    podPromise = (async () => {
      const { mountPodPreview } = await import('./arrivalPodPreview.js');
      const handle = await mountPodPreview({
        container: document.getElementById('arrival-camera-preview'),
        onFatal: () => {
          // WebGL context lost mid-session: the handle disposed itself.
          podPreview = null;
          if (podFatalCb) podFatalCb();
        },
      });
      // Enter can race the mount - don't keep a preview alive after collect().
      if (closed) {
        handle.dispose();
        return null;
      }
      podPreview = handle;
      // The pod is on screen - the "Camera preview appears here" overlay
      // would sit on top of it.
      const msg = document.getElementById('arrival-preview-msg');
      if (msg) msg.style.display = 'none';
      return handle;
    })().catch((err) => {
      console.warn('[Arrival] Pod preview unavailable, keeping flat preview:', err);
      return null;
    });
  }

  // --- presence ("who's already here") -------------------------------------
  // Cosmetic by design: every failure path (open access disabled, offline,
  // stale token, endpoint error) hides the section and never blocks Enter.
  const presenceBox = document.getElementById('arrival-presence');
  const presenceText = document.getElementById('arrival-presence-text');

  async function refreshPresence() {
    if (closed || document.visibilityState !== 'visible') return;
    const presence = await apiService.getRoomPresence();
    if (closed || !presenceBox) return;
    if (!presence) {
      presenceBox.style.display = 'none';
      return;
    }
    // Display names are hostile input - textContent only (presenceLine
    // deliberately does not escape).
    presenceText.textContent = formatPresenceLine(presence.live, presence.names);
    presenceBox.style.display = 'flex';
  }

  /**
   * The invite gate resolved and the form is visible. Starts the presence
   * poll (needs the room known up front) and hides the device section for
   * solo floor-plan previews, where a device check is pointless friction.
   * @param {{roomName?: string, preview?: boolean}} [opts]
   */
  function onGatePassed({ roomName, preview } = {}) {
    if (preview) {
      if (deviceGroup) deviceGroup.style.display = 'none';
      return;
    }
    if (meta.enabled === false) {
      if (deviceGroup) deviceGroup.style.display = 'none';
    } else {
      startPodPreview();
    }
    if (roomName && !presenceTimer) {
      // getRoomPresence scopes its token to sessionStore.roomName; the gate has
      // resolved the same room startExperience will use, so setting it early
      // only turns the presence line on.
      setIdentity({ roomName });
      refreshPresence();
      presenceTimer = setInterval(refreshPresence, PRESENCE_POLL_MS);
    }
  }

  // --- pre-event lobby (doors-open countdown) ------------------------------
  // Shown by loginFlow when verify-invite answers 403 EVENT_NOT_OPEN: the
  // invite is valid, the event just hasn't opened its doors. The countdown is
  // skew-corrected against the server clock (payload.serverNow) so a wrong
  // local clock can neither hold users hostage nor fire minutes early - the
  // server re-gates on re-verify either way.
  let lobbyTimer = null;

  function formatCountdown(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (days > 0) return `${days}d ${pad(h)}:${pad(m)}:${pad(s)}`;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  function formatLocal(iso) {
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short' }).format(
        new Date(iso),
      );
    } catch {
      return new Date(iso).toLocaleString();
    }
  }

  /**
   * Show the lobby hold state and start the countdown.
   * @param {Object} opts
   * @param {string} opts.title event title
   * @param {string} opts.startsAt ISO
   * @param {string} opts.doorsOpenAt ISO
   * @param {string} [opts.timezone] event IANA zone (label only)
   * @param {string} [opts.serverNow] ISO server clock at payload time
   * @param {() => void} opts.onDoorsOpen fired once when the countdown hits zero
   */
  function showLobby({ title, startsAt, doorsOpenAt, timezone, serverNow, onDoorsOpen }) {
    hideLobby();
    const lobby = document.getElementById('arrival-lobby');
    if (!lobby) {
      onDoorsOpen();
      return;
    }

    const titleEl = document.getElementById('arrival-lobby-title');
    const timesEl = document.getElementById('arrival-lobby-times');
    const countdownEl = document.getElementById('arrival-lobby-countdown');
    const noteEl = document.getElementById('arrival-lobby-note');

    titleEl.textContent = title || 'Your event';
    if (sessionTitle && title) {
      sessionTitle.textContent = title;
      sessionTitle.style.display = '';
    }
    const zoneSuffix = timezone ? ` (${timezone})` : '';
    timesEl.textContent =
      `Starts ${formatLocal(startsAt)}${zoneSuffix ? ` - event time zone${zoneSuffix}` : ''}. ` +
      `Doors open ${formatLocal(doorsOpenAt)}.`;
    noteEl.textContent =
      "You're on the list - this screen lets you in automatically when doors open.";
    lobby.style.display = '';

    // Server-minus-client clock offset at receipt; countdown runs in the
    // server's frame of reference.
    const skewMs = serverNow ? new Date(serverNow).getTime() - Date.now() : 0;
    const doorsMs = new Date(doorsOpenAt).getTime();
    let fired = false;
    const step = () => {
      const remaining = doorsMs - (Date.now() + skewMs);
      countdownEl.textContent = formatCountdown(remaining);
      if (remaining <= 0 && !fired) {
        fired = true;
        clearInterval(lobbyTimer);
        lobbyTimer = null;
        countdownEl.textContent = 'Doors are opening…';
        onDoorsOpen();
      }
    };
    step();
    if (!fired) lobbyTimer = setInterval(step, 1000);
  }

  function hideLobby() {
    if (lobbyTimer) {
      clearInterval(lobbyTimer);
      lobbyTimer = null;
    }
    const lobby = document.getElementById('arrival-lobby');
    if (lobby) lobby.style.display = 'none';
  }

  /**
   * Waiting-room host approval hold (docs/waiting-room-approval.md), shown
   * after Enter while a host decides. Distinct from showLobby(), which is the
   * pre-doors-open countdown and resolves on a clock rather than a person.
   * @param {Object} opts
   * @param {'waiting'|'denied'} opts.phase
   * @param {() => void} opts.onCancel Cancel (waiting) or Back (denied)
   */
  function showApproval({ phase, onCancel }) {
    const box = document.getElementById('arrival-approval');
    if (!box) return;
    const titleEl = document.getElementById('arrival-approval-title');
    const noteEl = document.getElementById('arrival-approval-note');
    const btn = /** @type {HTMLButtonElement} */ (
      document.getElementById('arrival-approval-cancel')
    );

    if (phase === 'denied') {
      titleEl.textContent = 'Not admitted';
      noteEl.textContent =
        "The host didn't let you in this time. Check with them if you think this is a mistake.";
      btn.textContent = 'Back';
    } else {
      titleEl.textContent = 'Waiting for the host to let you in';
      noteEl.textContent =
        "You're in the queue - this screen takes you in as soon as you're admitted.";
      btn.textContent = 'Cancel';
    }
    // Replace rather than add: the panel is reused across both phases.
    btn.onclick = onCancel;
    box.style.display = '';
  }

  function hideApproval() {
    const box = document.getElementById('arrival-approval');
    if (box) box.style.display = 'none';
  }

  /**
   * Gather everything for the join and release the hardware. Called by
   * loginFlow's Enter handler exactly once.
   */
  function collect() {
    closed = true;
    if (presenceTimer) {
      clearInterval(presenceTimer);
      presenceTimer = null;
    }
    // The random preselect becomes the real choice at Enter so the join
    // color always matches the highlighted swatch.
    setPlayerColor(selectedColor);
    const prefs = deviceCheck ? deviceCheck.getPrefs() : { audioOnly: false, micMuted: false };
    if (deviceCheck) {
      deviceCheck.teardown();
      deviceCheck = null;
    }
    if (podPreview) {
      podPreview.dispose();
      podPreview = null;
    }
    window.OGGI_DEVICE_PREFS = prefs;
    return { color: selectedColor, colorExplicitlyChosen, deviceCheckUsed, prefs };
  }

  return { onGatePassed, collect, showLobby, hideLobby, showApproval, hideApproval };
}
